import React, { useRef } from 'react';
import { observer, useAsObservableSource } from 'mobx-react-lite';
import { useAppStateContext } from '../../../AppState/AppState';
import {
    CampaignName,
    CampaignNameValue,
    ContentContainer,
    DatesContainer,
    DateTitle,
    DateValue,
    DateWrapper,
    TrashIconWrapper,
    ActiveLabel,
    ImageContainer,
    Row,
} from './HeroWidgetListItem.style';
import moment from 'moment';
import { Checkbox } from 'src/components/Checkbox/Checkbox';
import { observable, makeAutoObservable } from 'mobx';
import { HeroWidgetModelType } from 'src/api/config/hero_widget/get_hero_widget';
import { HeroWidgetState } from 'src/AppState/HeroWidgetState/HeroWidgetState';
import { Button } from 'src/domains/layouts/shared/buttons/Button';
import {
    CheckboxWrapper,
    ListItem,
    ActionsWrapper,
    ButtonsWrapper,
} from 'src/domains/layouts/shared/cms/list/List.style';
import { MoveUpDown } from 'src/domains/layouts/shared/moreActions/MoveUpDown';

interface HeroWidgetPropsType {
    heroWidget: HeroWidgetModelType;
}
class State {
    @observable copySuccess = false;
    @observable isShowImages = false;
    constructor(
        private readonly heroWidgetState: HeroWidgetState,
        private readonly heroWidget: HeroWidgetModelType,
        public readonly textAreaRef: React.RefObject<HTMLTextAreaElement>
    ) {
        makeAutoObservable(this);
    }

    onToggleShowImages = (): void => {
        this.isShowImages = !this.isShowImages;
    };

    onChangePageToEdit = (): void => {
        this.heroWidgetState.setPage('edit');
        this.heroWidgetState.setEditedHeroWidget(this.heroWidget);
    };

    onRemove = (): void => {
        this.heroWidgetState.onRemove(this.heroWidget.id);
    };

    changeActiveWidgetFlag = (): void => {
        this.heroWidgetState.setActiveWidgetFlag(this.heroWidget);
    };

    changeActivePromoFlag = (): void => {
        this.heroWidgetState.setActivePromoFlag(this.heroWidget);
    };

    moveItemUp = (): void => {
        this.heroWidgetState.moveItemUp(this.heroWidget.id);
    };

    moveItemDown = (): void => {
        this.heroWidgetState.moveItemDown(this.heroWidget.id);
    };
}

export const HeroWidgetListItem = observer(({ heroWidget }: HeroWidgetPropsType) => {
    const { heroWidgetState } = useAppStateContext();
    const heroWidgetObservable = useAsObservableSource(heroWidget);
    const textAreaRef = useRef<HTMLTextAreaElement>(null);
    const [state] = React.useState(() => new State(heroWidgetState, heroWidgetObservable, textAreaRef));
    const startDate = moment(heroWidget.date_from).format('DD-MM-YYYY HH:mm:ss');
    const endDate = moment(heroWidget.date_to).format('DD-MM-YYYY HH:mm:ss');
    const isEdited = heroWidget.id === heroWidgetState.editedHeroWidget?.id;

    const { changeActiveWidgetFlag, changeActivePromoFlag } = state;

    return (
        <ListItem
            isEdited={isEdited}
            homepage={false}
            boxHeight='274px'
            direction='row'
        >
            <ImageContainer imgSrc={heroWidget.header_img_small ?? ''} />
            <ContentContainer>
                <div>
                    <Row>
                        <CampaignName data-test='hero-widget-event-id'>Event Id: {heroWidget.event_id}</CampaignName>
                    </Row>
                    <Row>
                        <CampaignName data-test='hero-widget-market-id'>Market ID: {heroWidget.market_id}</CampaignName>
                    </Row>
                    <Row>
                        <CampaignName>Title </CampaignName>
                        <CampaignNameValue data-test='hero-widget-title'>{heroWidget.title}</CampaignNameValue>
                    </Row>
                    <Row>
                        <CampaignName>Subtitle </CampaignName>
                        <CampaignNameValue data-test='hero-widget-subtitle'>{heroWidget.subtitle}</CampaignNameValue>
                    </Row>
                    <Row>
                        <DatesContainer>
                            <DateWrapper isStart>
                                <DateTitle>Due Start</DateTitle>
                                <DateValue>{startDate}</DateValue>
                            </DateWrapper>
                            <DateWrapper>
                                <DateTitle>Due Finish</DateTitle>
                                <DateValue>{endDate}</DateValue>
                            </DateWrapper>
                        </DatesContainer>
                    </Row>
                </div>
                <div>
                    <CheckboxWrapper>
                        <Checkbox
                            labelSide='right'
                            onChange={changeActiveWidgetFlag}
                            isChecked={heroWidget.is_active_widget}
                            isDisabled={false}
                            dataTest='hero-widget-checkbox'
                        >
                            <ActiveLabel>Active Widget</ActiveLabel>
                        </Checkbox>
                        <Checkbox
                            labelSide='right'
                            onChange={changeActivePromoFlag}
                            isChecked={heroWidget.is_active_promo}
                            isDisabled={false}
                            dataTest='hero-widget-promo-checkbox'
                        >
                            <ActiveLabel>Active Promo</ActiveLabel>
                        </Checkbox>
                    </CheckboxWrapper>
                    <ButtonsWrapper>
                        <MoveUpDown
                            moveItemUp={state.moveItemUp}
                            moveItemDown={state.moveItemDown}
                        />
                        <ActionsWrapper>
                            <Button
                                version='secondary'
                                size='small'
                                onClick={state.onChangePageToEdit}
                            >
                                Edit
                            </Button>
                            <TrashIconWrapper onClick={state.onRemove} />
                        </ActionsWrapper>
                    </ButtonsWrapper>
                </div>
            </ContentContainer>
        </ListItem>
    );
});
